import React from "react";

// Modern Team Detail Layout Component
export const TeamDetailModernLayout = ({ teamMember }) => {
  // Helper to get key responsibilities (from activities_skills or custom data)
  const getKeyResponsibilities = () => {
    if (teamMember?.key_responsibilities && teamMember.key_responsibilities.length > 0) {
      return teamMember.key_responsibilities;
    }
    // Fallback to activities_skills if available
    if (teamMember?.activities_skills && teamMember.activities_skills.length > 0) {
      return teamMember.activities_skills.map(item => item.heading || item.desc);
    }
    // Placeholder data
    return [
      "Strategic leadership and vision setting",
      "Team development and mentorship",
      "Stakeholder engagement and partnerships",
      "Innovation and process improvement"
    ];
  };

  // Helper to get areas of focus (tags/chips)
  const getAreasOfFocus = () => {
    if (teamMember?.areas_of_focus && teamMember.areas_of_focus.length > 0) {
      return teamMember.areas_of_focus;
    }
    // Fallback to professional_skills if available
    if (teamMember?.professional_skills && teamMember.professional_skills.length > 0) {
      return teamMember.professional_skills.map(skill => skill.heading);
    }
    // Placeholder data based on role
    const roleBasedTags = {
      "CEO": ["Strategic Planning", "Leadership", "Innovation", "Growth"],
      "Director": ["Operations", "Strategy", "Management", "Execution"],
      "Chairperson": ["Governance", "Strategy", "Advisory", "Leadership"],
      "Advisor": ["Consulting", "Expertise", "Guidance", "Strategy"]
    };
    return roleBasedTags[teamMember?.role] || ["Leadership", "Strategy", "Innovation"];
  };

  // Helper to get highlights/key contributions
  const getHighlights = () => {
    if (teamMember?.highlights && teamMember.highlights.length > 0) {
      return teamMember.highlights;
    }
    if (teamMember?.key_contributions && teamMember.key_contributions.length > 0) {
      return teamMember.key_contributions;
    }
    // Return null if no highlights (optional section)
    return null;
  };

  // Get short bio (4-6 lines) – prefer full "about" for detail page
  const getShortBio = () => {
    if (teamMember?.about) {
      return teamMember.about;
    }
    if (teamMember?.short_bio) {
      return teamMember.short_bio;
    }
    // Use description if available, truncate if too long
    if (teamMember?.description) {
      const sentences = teamMember.description.split(/[.!?]+/).filter(s => s.trim().length > 0);
      return sentences.slice(0, 3).join(". ") + ".";
    }
    // Placeholder
    return "Experienced leader with a proven track record in strategic planning and team development. Passionate about innovation and driving organizational growth through collaborative leadership.";
  };

  // Get 1-line intro
  const getOneLineIntro = () => {
    if (teamMember?.one_line_intro) {
      return teamMember.one_line_intro;
    }
    if (teamMember?.tagline) {
      return teamMember.tagline;
    }
    // Generate from role and description
    const role = teamMember?.role || "Team Member";
    return `${role} dedicated to driving excellence and innovation.`;
  };

  const keyResponsibilities = getKeyResponsibilities();
  const areasOfFocus = getAreasOfFocus();
  const highlights = getHighlights();
  const shortBio = getShortBio();
  const oneLineIntro = getOneLineIntro();

  return (
    <div className="team-detail-modern">
      {/* Profile Header */}
      <section className="profile-header-section" style={{ 
        background: "#ffffff",
        paddingTop: "3rem",
        paddingBottom: "3rem"
      }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="profile-header-card" style={{
                background: "#ffffff",
                borderRadius: "16px",
                padding: "2.5rem",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                border: "1px solid #e2e8f0"
              }}>
                <div className="row align-items-center">
                  <div className="col-md-auto text-center text-md-left mb-4 mb-md-0">
                    <div
                      className="profile-photo"
                      style={{
                        width: "140px",
                        height: "140px",
                        borderRadius: "50%",
                        backgroundImage: `url(${teamMember?.image || '/assets/img/dumy-person.jpg'})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        border: "4px solid #f7fafc",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                        margin: "0 auto"
                      }}
                    />
                  </div>
                  <div className="col-md">
                    <div className="profile-info" style={{ textAlign: "center" }}>
                      <h1 className="profile-name" style={{
                        fontSize: "2rem",
                        fontWeight: "700",
                        color: "#1a202c",
                        marginBottom: "0.5rem",
                        lineHeight: "1.2"
                      }}>
                        {teamMember?.name || "Team Member"}
                      </h1>
                      <p className="profile-title" style={{
                        fontSize: "1.125rem",
                        color: "#667eea",
                        fontWeight: "600",
                        marginBottom: "0.75rem"
                      }}>
                        {teamMember?.role || "Role"}
                      </p>
                      <p className="profile-intro" style={{
                        fontSize: "1rem",
                        color: "#4a5568",
                        lineHeight: "1.6",
                        marginBottom: "0"
                      }}>
                        {oneLineIntro}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Short Bio */}
      <section className="bio-section" style={{
        background: "#f7fafc",
        paddingTop: "2.5rem",
        paddingBottom: "2.5rem"
      }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="bio-card" style={{
                background: "#ffffff",
                borderRadius: "12px",
                padding: "2rem",
                boxShadow: "0 1px 8px rgba(0,0,0,0.06)"
              }}>
                <h2 className="section-title" style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  color: "#1a202c",
                  marginBottom: "1.25rem"
                }}>
                  About
                </h2>
                <p className="bio-text" style={{
                  fontSize: "1rem",
                  color: "#4a5568",
                  lineHeight: "1.8",
                  marginBottom: "0"
                }}>
                  {shortBio}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Responsibilities */}
      <section className="responsibilities-section" style={{
        background: "#ffffff",
        paddingTop: "2.5rem",
        paddingBottom: "2.5rem"
      }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="responsibilities-card" style={{
                background: "#ffffff",
                borderRadius: "12px",
                padding: "2rem",
                boxShadow: "0 1px 8px rgba(0,0,0,0.06)"
              }}>
                <h2 className="section-title" style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  color: "#1a202c",
                  marginBottom: "1.5rem"
                }}>
                  Key Responsibilities
                </h2>
                <ul className="responsibilities-list" style={{
                  listStyle: "none",
                  padding: "0",
                  margin: "0"
                }}>
                  {keyResponsibilities.slice(0, 6).map((responsibility, index) => (
                    <li key={index} style={{
                      padding: "0.75rem 0",
                      paddingLeft: "1.75rem",
                      position: "relative",
                      fontSize: "1rem",
                      color: "#4a5568",
                      lineHeight: "1.7",
                      borderBottom: index < keyResponsibilities.length - 1 ? "1px solid #e2e8f0" : "none"
                    }}>
                      <span style={{
                        position: "absolute",
                        left: "0",
                        top: "0.875rem",
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        background: "#667eea"
                      }}></span>
                      {typeof responsibility === 'string' ? responsibility : (responsibility.heading || responsibility.desc)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas of Focus */}
      <section className="focus-section" style={{
        background: "#f7fafc",
        paddingTop: "2.5rem",
        paddingBottom: "2.5rem"
      }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="focus-card" style={{
                background: "#ffffff",
                borderRadius: "12px",
                padding: "2rem",
                boxShadow: "0 1px 8px rgba(0,0,0,0.06)"
              }}>
                <h2 className="section-title" style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  color: "#1a202c",
                  marginBottom: "1.5rem"
                }}>
                  Areas of Focus
                </h2>
                <div className="focus-tags" style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.75rem"
                }}>
                  {areasOfFocus.map((area, index) => (
                    <span
                      key={index}
                      className="focus-tag"
                      style={{
                        display: "inline-block",
                        padding: "0.5rem 1.25rem",
                        background: "#edf2f7",
                        color: "#2d3748",
                        borderRadius: "24px",
                        fontSize: "0.9rem",
                        fontWeight: "500",
                        border: "1px solid #e2e8f0",
                        transition: "all 0.2s ease"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "#667eea";
                        e.target.style.color = "#ffffff";
                        e.target.style.borderColor = "#667eea";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "#edf2f7";
                        e.target.style.color = "#2d3748";
                        e.target.style.borderColor = "#e2e8f0";
                      }}
                    >
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights / Key Contributions (Optional) */}
      {highlights && highlights.length > 0 && (
        <section className="highlights-section" style={{
          background: "#ffffff",
          paddingTop: "2.5rem",
          paddingBottom: "2.5rem"
        }}>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10">
                <div className="highlights-card" style={{
                  background: "#ffffff",
                  borderRadius: "12px",
                  padding: "2rem",
                  boxShadow: "0 1px 8px rgba(0,0,0,0.06)"
                }}>
                  <h2 className="section-title" style={{
                    fontSize: "1.5rem",
                    fontWeight: "700",
                    color: "#1a202c",
                    marginBottom: "1.5rem"
                  }}>
                    Key Contributions
                  </h2>
                  <div className="highlights-list">
                    {highlights.slice(0, 3).map((highlight, index) => (
                      <div
                        key={index}
                        className="highlight-item"
                        style={{
                          padding: "1.25rem",
                          background: "#f7fafc",
                          borderRadius: "8px",
                          marginBottom: index < highlights.length - 1 ? "1rem" : "0",
                          borderLeft: "4px solid #667eea"
                        }}
                      >
                        <p style={{
                          fontSize: "1rem",
                          color: "#4a5568",
                          lineHeight: "1.7",
                          margin: "0"
                        }}>
                          {typeof highlight === 'string' ? highlight : (highlight.title || highlight.description || highlight)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Social Links */}
      <section className="social-section" style={{
        background: "#f7fafc",
        paddingTop: "2.5rem",
        paddingBottom: "2.5rem"
      }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="social-card" style={{
                background: "#ffffff",
                borderRadius: "12px",
                padding: "2rem",
                boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
                textAlign: "center"
              }}>
                <h2 className="section-title" style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  color: "#1a202c",
                  marginBottom: "1.5rem"
                }}>
                  Connect
                </h2>
                <div className="social-links" style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "1rem",
                  flexWrap: "wrap",
                  marginBottom: "1.5rem"
                }}>
                  {teamMember?.social?.linkedin && (
                    <a
                      href={teamMember.social.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-link linkedin"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "48px",
                        height: "48px",
                        borderRadius: "50%",
                        background: "#0077b5",
                        color: "#ffffff",
                        fontSize: "1.25rem",
                        textDecoration: "none",
                        transition: "all 0.2s ease",
                        boxShadow: "0 2px 8px rgba(0,119,181,0.3)"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow = "0 4px 12px rgba(0,119,181,0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = "0 2px 8px rgba(0,119,181,0.3)";
                      }}
                    >
                      <i className="fab fa-linkedin-in"></i>
                    </a>
                  )}
                  {teamMember?.contact?.email && (
                    <a
                      href={`mailto:${teamMember.contact.email}`}
                      className="social-link email"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "48px",
                        height: "48px",
                        borderRadius: "50%",
                        background: "#667eea",
                        color: "#ffffff",
                        fontSize: "1.25rem",
                        textDecoration: "none",
                        transition: "all 0.2s ease",
                        boxShadow: "0 2px 8px rgba(102,126,234,0.3)"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "translateY(-2px)";
                        e.target.style.boxShadow = "0 4px 12px rgba(102,126,234,0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = "0 2px 8px rgba(102,126,234,0.3)";
                      }}
                    >
                      <i className="fas fa-envelope"></i>
                    </a>
                  )}
                </div>
                {teamMember?.contact?.email && (
                  <a
                    href={`mailto:${teamMember.contact.email}`}
                    className="contact-button"
                    style={{
                      display: "inline-block",
                      padding: "0.75rem 2rem",
                      background: "#667eea",
                      color: "#ffffff",
                      borderRadius: "8px",
                      textDecoration: "none",
                      fontWeight: "600",
                      fontSize: "0.95rem",
                      transition: "all 0.2s ease",
                      border: "none"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#5568d3";
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = "0 4px 12px rgba(102,126,234,0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#667eea";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    Contact
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Responsive Styles */}
      <style>{`
        @media (min-width: 768px) {
          .profile-info {
            text-align: left !important;
          }
        }
        @media (max-width: 768px) {
          .profile-header-card {
            padding: 1.5rem !important;
          }
          .profile-name {
            font-size: 1.75rem !important;
          }
          .profile-title {
            font-size: 1rem !important;
          }
          .section-title {
            font-size: 1.25rem !important;
          }
          .bio-card,
          .responsibilities-card,
          .focus-card,
          .highlights-card,
          .social-card {
            padding: 1.5rem !important;
          }
          .cta-title {
            font-size: 1.5rem !important;
          }
          .cta-description {
            font-size: 1rem !important;
          }
          .cta-buttons {
            flex-direction: column;
            align-items: stretch;
          }
          .cta-button {
            width: 100%;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};
